import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Loader2, Plus, Users } from "lucide-react";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ProjectAccessDialog } from "./project-access-dialog";
import { useCreateProject } from "./use-create-project";

const NO_PROJECT_MANAGER_VALUE = "__no_project_manager__";

interface ProjectCreateDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCreated?: (projectId: string) => void | Promise<void>;
  title?: string;
  description?: string;
  confirmLabel?: string;
  inputLabel?: string;
  inputPlaceholder?: string;
  showDetailsFields?: boolean;
  trigger?: ReactNode;
}

export function ProjectCreateDialog({
  open,
  onOpenChange,
  onCreated,
  title = "Create Project",
  description = "Enter a name for your new project.",
  confirmLabel = "Create Project",
  inputLabel = "Project Name",
  inputPlaceholder = "Enter project name",
  showDetailsFields = false,
  trigger,
}: ProjectCreateDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState("");
  const [projectNumber, setProjectNumber] = useState("");
  const [projectAddress, setProjectAddress] = useState("");
  const [projectManagerUserId, setProjectManagerUserId] = useState("");
  const [projectAccessUserIds, setProjectAccessUserIds] = useState<string[]>([]);
  const [isAccessDialogOpen, setIsAccessDialogOpen] = useState(false);
  const { createProject, isCreating } = useCreateProject();
  const { data: orgMembers } = useQuery(
    convexQuery(api.auth.listOrganizationMembersMinimal, showDetailsFields ? {} : "skip")
  );

  const dialogOpen = open ?? uncontrolledOpen;
  const setDialogOpen = onOpenChange ?? setUncontrolledOpen;
  const organizationMembers = orgMembers ?? [];
  const selectedAccessMembers = organizationMembers.filter((member) =>
    projectAccessUserIds.includes(member._id)
  );

  const resetForm = () => {
    setNewProjectName("");
    setProjectNumber("");
    setProjectAddress("");
    setProjectManagerUserId("");
    setProjectAccessUserIds([]);
    setIsAccessDialogOpen(false);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setDialogOpen(nextOpen);
    if (!nextOpen) {
      resetForm();
    }
  };

  const handleCreateProject = async () => {
    const trimmedName = newProjectName.trim();
    if (!trimmedName) {
      return;
    }

    const projectId = await createProject({
      name: trimmedName,
      projectNumber: projectNumber.trim() || undefined,
      projectAddress: projectAddress.trim() || undefined,
      projectManagerUserId: projectManagerUserId
        ? (projectManagerUserId as Id<"users">)
        : undefined,
      projectAccessUserIds: projectAccessUserIds as Id<"users">[],
    });
    if (!projectId) return;
    await onCreated?.(projectId);
    handleOpenChange(false);
  };

  return (
    <>
      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
        <DialogContent className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="project-name">{inputLabel}</Label>
              <Input
                id="project-name"
                placeholder={inputPlaceholder}
                value={newProjectName}
                onChange={(event) => setNewProjectName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void handleCreateProject();
                  }
                }}
                autoComplete="off"
                disabled={isCreating}
                autoFocus
              />
            </div>
            {showDetailsFields ? (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="project-number">Project Number</Label>
                    <Input
                      id="project-number"
                      placeholder="Enter project number"
                      value={projectNumber}
                      onChange={(event) => setProjectNumber(event.target.value)}
                      disabled={isCreating}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project-manager">Project Manager</Label>
                    <Select
                      value={projectManagerUserId || NO_PROJECT_MANAGER_VALUE}
                      onValueChange={(value) =>
                        setProjectManagerUserId(
                          value === NO_PROJECT_MANAGER_VALUE ? "" : value
                        )
                      }
                      disabled={isCreating}
                    >
                      <SelectTrigger id="project-manager">
                        <SelectValue placeholder="Select project manager" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_PROJECT_MANAGER_VALUE}>
                          No project manager
                        </SelectItem>
                        {organizationMembers.map((member) => (
                          <SelectItem key={member._id} value={member._id}>
                            {member.name} ({member.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="project-address">Project Address</Label>
                  <Input
                    id="project-address"
                    placeholder="Enter project address"
                    value={projectAddress}
                    onChange={(event) => setProjectAddress(event.target.value)}
                    disabled={isCreating}
                    autoComplete="off"
                  />
                </div>
                <div className="space-y-3 rounded-lg border px-3 py-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 space-y-1">
                      <Label>Project Access</Label>
                      <p className="text-xs text-muted-foreground">PM included automatically.</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsAccessDialogOpen(true)}
                      disabled={isCreating || organizationMembers.length === 0}
                    >
                      <Plus className="size-4" />
                      Add Access
                    </Button>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="size-4" />
                    <span>
                      {selectedAccessMembers.length} additional member
                      {selectedAccessMembers.length === 1 ? "" : "s"} selected.
                    </span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => void handleCreateProject()}
              disabled={isCreating || !newProjectName.trim()}
            >
              {isCreating ? <Loader2 className="size-4 animate-spin" /> : null}
              {confirmLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {showDetailsFields ? (
        <ProjectAccessDialog
          open={isAccessDialogOpen}
          onOpenChange={setIsAccessDialogOpen}
          projectName={newProjectName.trim() || "New Project"}
          members={organizationMembers}
          selectedUserIds={projectAccessUserIds}
          onSelectedUserIdsChange={setProjectAccessUserIds}
          lockedUserIds={projectManagerUserId ? [projectManagerUserId] : []}
          lockedUserLabel="project manager"
        />
      ) : null}
    </>
  );
}
